import * as React from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';

import { Panel, PopupAction, Modal, Button, Loader } from '@teamsnap/teamsnap-ui';
import { TEAMSNAP_URL } from '../../core/constants';
import * as GqlSeasons from '../../core/gql/queries/seasons';
import * as HistoricalSeasonsQuery from '../../core/gql/queries/historical-seasons';
import * as DeleteSeasonValidationQuery from '../../core/gql/queries/detele_season_validation';
import * as GqlArchiveSeasonMutation from '../../core/gql/mutations/archive-season';
import * as GqlDeleteSeasonMutation from '../../core/gql/mutations/delete-season';
import Season from '../../models/season';
import Program from '../../models/program';
import { ActivitySeasonType } from '../../models/season';
import { reactiveFeedbackMessage } from '../../core/apollo-cache';
import SeasonDeleteModal from './season-delete-modal';
export interface Props {
  season: Season;
  programName: String;
  // TODO: this is being manually pushed as a prop in an effort to move away from mobx injection.
  // Eventually, appStateStore should no longer be needed in favor of apollo cache.
  selectSeasonIdCallback?: (type: ActivitySeasonType, id: number | null) => void;
  canUserDeleteSeason: boolean;
  organizationId?: number;
  showArchived?: boolean;
}

/**
 * SeasonCard displays a card with season information, including actions that modify that season.
 * @param Props
 *  - @see interface Props above
 */
const SeasonCard: React.FunctionComponent<Props> = ({
  season,
  programName,
  selectSeasonIdCallback,
  canUserDeleteSeason,
  organizationId,
  showArchived,
}) => {
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [isDeletable, setIsDeletable] = React.useState(false);
  const showSeason = (type: ActivitySeasonType, id?: number) => {
    selectSeasonIdCallback(type, id);
  };

  const refetchSeasonsQuery = {
    query: GqlSeasons.QUERY,
    variables: { programId: season.programId, isArchived: showArchived ? null : false }
  };
  const refetchHistoricalSeasonsQuery = {
    query: HistoricalSeasonsQuery.QUERY,
    variables: { organizationId: organizationId },
  };
  const isHistoricalSeason = season.type === ActivitySeasonType.LegacyClubAndLeague;

  const [archiveSeason] = useMutation(GqlArchiveSeasonMutation.MUTATION, {
    refetchQueries: [isHistoricalSeason ? refetchHistoricalSeasonsQuery : refetchSeasonsQuery],
  });

  const [deleteSeasonValidation, { networkStatus, loading }] = useLazyQuery(DeleteSeasonValidationQuery.QUERY, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const [deleteSeason] = useMutation(GqlDeleteSeasonMutation.MUTATION, {
    refetchQueries: [refetchSeasonsQuery],
  });

  // TODO: Is there a way to do this without the store at this context?
  // Like a cache lookup against the season ID? Can we guarantee it'll be there?
  const seasonName = season.name;

  const deleteValidation = () => {
    setShowDeleteModal(true);

    deleteSeasonValidation({
      variables: { seasonId: season.id },
    }).then(({ data }) => {
      if (data?.seasonDeleteValidation?.isDeletable) {
        setIsDeletable(true);
      } else {
        setIsDeletable(false);
      }
    });
  };

  const performAction = async (action: string) => {
    try {
      if (action === 'delete') {
        await deleteSeason({
          variables: {
            id: season.id,
          },
        });
      } else {
        await archiveSeason({
          variables: {
            id: season.id,
          },
        });
      }

      reactiveFeedbackMessage({
        message: `${programName} ${seasonName} has been ${action}d.`,
        isError: false,
        autoclose: true,
      });
    } catch (e) {
      reactiveFeedbackMessage({
        message: `Oops!  We couldn't ${action} ${programName} ${seasonName}. Please try again.`,
        isError: true,
        autoclose: true,
      });
    }
  };

  let actions: any[] = isHistoricalSeason ? [] : [{ text: 'Edit', callback: () => showSeason(season.type, season.id) }];

  if (!season.isArchived) {
    actions = [
      ...actions,
      {
        text: 'Archive',
        callback: () => performAction('archive'),
        requiresConfirmation: true,
        confirmationText:
          'Archiving will hide the teams in this season from the mobile apps for all members. Are you sure you want to do this?',
      },
    ];
  }
  const showDeleteOption = !isHistoricalSeason && canUserDeleteSeason;
  if (showDeleteOption) {
    actions = [
      ...actions,
      {
        text: 'Delete',
        callback: () => deleteValidation(),
      },
    ];
  }

  return (
    <>
      <Panel mods="season-card" key={`season-card-${season.id}`} testId="seasonCard">
        <div className="u-padMd u-xs-padSidesLg u-flex u-flexAlignItemsCenter u-flexJustifyBetween" data-testid={`seasonTile-${season.name}`}>
          <div className="Grid">
            <div className="Grid-cell u-xs-size1of2">
              <div className="u-spaceBottomSm u-fontSizeXl">
                {season.tsDivisionId && (
                  <a href={`${TEAMSNAP_URL}/team/set_division/${season.tsDivisionId}`} data-testid={`season-${season.name}`}>{season.name}</a>
                )}
                {!season.tsDivisionId && season.name}
              </div>
              <p className="u-colorGrey" data-testid="isArchived">
                {season.isArchived ? 'Archived' : 'Active'}
                {season.type == ActivitySeasonType.ClubSeason ? (
                  ''
                ) : (
                  <span className="u-spaceLeftSm" data-testid="seasonType">{`(${
                    season.type === ActivitySeasonType.LegacyClubAndLeague ? 'Historical' : 'Legacy Season'
                  })`}
                  </span>
                )}
              </p>
            </div>
            {actions.length > 0 && (
              <div className="Grid-cell u-xs-size1of2 u-textRight u-flex u-flexCol u-flexJustifyBetween">
                <div className="button-list" data-testid="threeDotsButtonSection">
                  <PopupAction
                    direction={['down', 'right']}
                    text="..."
                    actions={actions}
                    popupStyle={{ width: '150px' }}
                    testId="threeDotsHoverButton"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Panel>
      {showDeleteOption && (
        <SeasonDeleteModal
          season={season}
          performAction={performAction}
          setShowDeleteModal={setShowDeleteModal}
          showDeleteModal={showDeleteModal}
          loading={loading}
          isDeletable={isDeletable}
        />
      )}
    </>
  );
};

export default SeasonCard;
